import { useNavigate } from "react-router-dom";
import { Plus, Minus, Heart, Bike, Star, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { api, inr } from "@/lib/api";

const FALLBACK_IMG = "/rani-brand.png";

export function QtyStepper({ qty, onInc, onDec, testid }) {
  return (
    <div data-testid={testid} className="flex items-center justify-between bg-emerald-700 text-white rounded-xl px-1.5 py-1 font-bold text-sm shadow-sm w-full">
      <button data-testid={`${testid}-dec`} onClick={onDec} className="p-1 hover:bg-emerald-800 rounded-lg transition-colors"><Minus size={16} /></button>
      <span className="min-w-[24px] text-center">{qty}</span>
      <button data-testid={`${testid}-inc`} onClick={onInc} className="p-1 hover:bg-emerald-800 rounded-lg transition-colors"><Plus size={16} /></button>
    </div>
  );
}

export default function ProductCard({ product, onWishlist, wished, isNew }) {
  const nav = useNavigate();
  const { qtyOf, add, setQty } = useCart();
  const qty = qtyOf(product.id);
  const off = product.mrp > product.price ? Math.round((1 - product.price / product.mrp) * 100) : 0;
  const oos = !product.in_stock;
  const notifyMe = async (e) => {
    e.stopPropagation();
    try { await api.post(`/products/${product.id}/notify-me`); toast.success("We'll notify you when it's back in stock"); }
    catch (err) { if (err?.response?.status === 401) nav("/login"); else toast.error("Could not register"); }
  };

  return (
    <div data-testid={`product-card-${product.id}`} className="group relative bg-white border border-slate-100 rounded-2xl p-3 flex flex-col justify-between hover:shadow-lg hover:border-emerald-200 transition-all duration-200 animate-fade-up">
      {isNew && (
        <span data-testid={`new-badge-${product.id}`} className="absolute top-2 left-2 z-10 bg-gradient-to-r from-red-600 to-rose-500 text-white text-[10px] font-extrabold px-2 py-0.5 rounded-md shadow-md tracking-wide animate-pulse">✦ NEW</span>
      )}
      {off > 0 && <span className={`absolute ${isNew ? "top-9" : "top-2"} left-2 z-10 bg-[#33582f] text-white text-[11px] font-extrabold px-2 py-0.5 rounded-md shadow`}>{off}% OFF</span>}
      {onWishlist && (
        <button data-testid={`wishlist-toggle-${product.id}`} onClick={(e) => { e.stopPropagation(); onWishlist(product); }}
          className="absolute top-2 right-2 z-10 bg-white/90 backdrop-blur rounded-full p-1.5 shadow-sm hover:scale-110 transition-transform">
          <Heart size={15} className={wished ? "fill-red-500 text-red-500" : "text-slate-400"} />
        </button>
      )}
      <div onClick={() => nav(`/product/${product.slug || product.id}`)} className="cursor-pointer">
        <div className="aspect-square rounded-xl overflow-hidden bg-slate-50 mb-2 flex items-center justify-center p-2 border border-slate-100/80">
          <img
            src={product.image || FALLBACK_IMG}
            alt={product.name}
            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = FALLBACK_IMG; }}
            onLoad={(e) => { const t = e.currentTarget; if (t.naturalWidth && t.naturalWidth <= 2 && !t.src.endsWith(FALLBACK_IMG)) { t.src = FALLBACK_IMG; t.className = "w-full h-full object-contain p-2 opacity-80"; } }}
            className={`w-full h-full ${product.image ? "object-cover group-hover:scale-105" : "object-contain p-2 opacity-80"} transition-transform duration-300`}
            loading="lazy"
          />
        </div>
        <p className="text-[11px] text-slate-400 font-medium">{product.unit}</p>
        <h3 className="text-sm font-semibold text-slate-800 line-clamp-2 leading-snug min-h-[2.5rem]">{product.name}</h3>
        {(product.local_delivery || !product.seller_id) && (
          <span data-testid={`local-delivery-${product.id}`} className="inline-flex items-center gap-1 mt-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5">
            <Bike size={11} /> Local Delivery
          </span>
        )}
        {product.seller_gst_verified && (
          <span data-testid={`gst-verified-${product.id}`} title="GST Verified · trusted seller" className="inline-flex items-center gap-1 mt-1 ml-1 text-[10px] font-bold text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-1.5 py-0.5">
            <ShieldCheck size={11} /> GST Verified
          </span>
        )}
        {product.rating_count > 0 && (
          <div className="flex items-center gap-1 mt-1" data-testid={`rating-${product.id}`}>
            <span className="inline-flex items-center gap-0.5 bg-emerald-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded"><Star size={9} className="fill-white" /> {product.rating_avg}</span>
            <span className="text-[10px] text-slate-400">({product.rating_count})</span>
          </div>
        )}
      </div>
      <div className="mt-2 flex items-end justify-between gap-2">
        <div className="min-w-0">
          <span className="text-base font-bold text-slate-900">{inr(product.price)}</span>
          {off > 0 && <span className="text-xs text-slate-400 line-through ml-1">{inr(product.mrp)}</span>}
        </div>
        <div className="w-[92px] shrink-0">
          {oos ? (
            <div className="space-y-1">
              <span data-testid={`oos-${product.id}`} className="block text-center text-[11px] font-bold text-red-500 border border-red-200 rounded-xl py-1.5">Out of stock</span>
              <button data-testid={`notify-${product.id}`} onClick={notifyMe} className="w-full py-1 text-[10px] font-bold text-emerald-700 border border-emerald-300 rounded-xl hover:bg-emerald-50 transition-colors">NOTIFY ME</button>
            </div>
          ) : qty > 0 ? (
            <QtyStepper testid={`stepper-${product.id}`} qty={qty} onInc={() => setQty(product.id, qty + 1)} onDec={() => setQty(product.id, qty - 1)} />
          ) : (
            <button data-testid={`add-to-cart-${product.id}`} onClick={() => add(product)}
              className="w-full py-2 bg-emerald-50 hover:bg-emerald-600 text-emerald-700 hover:text-white border border-emerald-200 rounded-xl font-bold text-xs transition-colors">
              ADD
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
